import { BOOK_NAMES } from '../utils/bibleBooks';
import { BIBLE_VERSE_COUNTS } from './verseCounts';

// ── Reading-page verse lines → book / chapter / verse ───────────────────────
// The daily `verses` rows carry each verse as one line led by its reference,
// written differently per language and source:
//   "Col 3 :12 text"   English (space before the colon)
//   "Rom. 12:1 text"   Spanish with a dot;  "1 Co. 3:1 text" numbered book
//   "S.S. 1:1 text"    Spanish Song of Songs
//   "西 3:12 text"      Chinese one- or multi-character abbreviation
//   "Jd 1 text"        single-chapter book, verse number only
//   "15:1 text"        a few English rows that dropped the book entirely
// bible_chapters was built from these same lines by
// scripts/archive/buildBibleChapters.js, so this follows its parser exactly:
// a reference read the same way here lands on the chapter row it built.

const ABBR_TO_BOOK = {
  // Traditional Chinese
  '太':'Mt','可':'Mk','路':'Lk','約':'Jn','徒':'Acts',
  '羅':'Rm','林前':'1Co','林後':'2Co','加':'Gal','弗':'Eph',
  '腓':'Phil','西':'Col','帖前':'1Th','帖後':'2Th',
  '提前':'1Ti','提後':'2Ti','多':'Tit','門':'Phm','來':'Heb',
  '雅':'Jas','彼前':'1Pe','彼後':'2Pe',
  '約一':'1Jn','約二':'2Jn','約三':'3Jn','猶':'Jude','啟':'Rev',
  '創':'Gen','出':'Exo','利':'Lev','民':'Num','申':'Deut',
  '書':'Josh','士':'Judg','得':'Ruth',
  '撒上':'1Sam','撒下':'2Sam','王上':'1Kgs','王下':'2Kgs',
  '代上':'1Chr','代下':'2Chr',
  '拉':'Ezra','尼':'Neh','斯':'Esth','伯':'Job',
  '詩':'Ps','箴':'Prov','傳':'Eccl','歌':'Song',
  '賽':'Isa','耶':'Jer','哀':'Lam','結':'Ezek','但':'Dan',
  '何':'Hos','珥':'Joel','摩':'Amos','俄':'Obad',
  '拿':'Jonah','彌':'Mic','鴻':'Nah','哈':'Hab',
  '番':'Zeph','該':'Hag','亞':'Zech','瑪':'Mal',
  // Simplified Chinese differences
  '约':'Jn','罗':'Rm','林后':'2Co','帖后':'2Th',
  '提后':'2Ti','门':'Phm','来':'Heb','彼后':'2Pe',
  '约一':'1Jn','约二':'2Jn','约三':'3Jn','犹':'Jude','启':'Rev',
  '创':'Gen','书':'Josh','诗':'Ps','传':'Eccl',
  '赛':'Isa','结':'Ezek','弥':'Mic','鸿':'Nah',
  '该':'Hag','亚':'Zech','玛':'Mal',
  // Spanish (dots and spaces are stripped before lookup)
  'Ro':'Rm','Rom':'Rm','Hch':'Acts','Lc':'Lk','Mc':'Mk',
  'Ef':'Eph','Fil':'Phil','Flm':'Phm','Stg':'Jas','Ap':'Rev',
  'Ex':'Exo','Nu':'Num','Jos':'Josh','Jue':'Judg','Rut':'Ruth','Rt':'Ruth',
  '1Re':'1Kgs','2Re':'2Kgs','1Cr':'1Chr','2Cr':'2Chr',
  'Esd':'Ezra','Est':'Esth','Sal':'Ps','Ecl':'Eccl',
  'Cant':'Song','Cnt':'Song','Cantar':'Song',
  'Is':'Isa','Ez':'Ezek','Os':'Hos','Abd':'Obad','Jon':'Jonah',
  'Mi':'Mic','Sof':'Zeph','Zac':'Zech','Jud':'Jude',
  '1Ts':'1Th','2Ts':'2Th','1Tes':'1Th','2Tes':'2Th','1Thes':'1Th','2Thes':'2Th',
  '1King':'1Kgs','2King':'2Kgs','1Rey':'1Kgs','2Rey':'2Kgs',
  '1Tim':'1Ti','2Tim':'2Ti',
  // Short English abbreviations used in the data
  'Jd':'Jude','Ac':'Acts','Co':'Col','Ep':'Eph','Ga':'Gal',
  'He':'Heb','Ja':'Jas','Pp':'Phil','Rv':'Rev','Tt':'Tit',
  '1J':'1Jn','2J':'2Jn','3J':'3Jn','1P':'1Pe','2P':'2Pe',
  'Dn':'Dan','Dt':'Deut','Ec':'Eccl','Es':'Esth',
  'Ezk':'Ezek','Ezr':'Ezra','Gn':'Gen',
  'Hb':'Hab','Hg':'Hag','Ho':'Hos',
  'Jb':'Job','Je':'Jer','Jg':'Judg',
  'Jl':'Joel','Jnh':'Jonah','Js':'Josh','La':'Lam',
  'Lv':'Lev','Ml':'Mal','Na':'Nah','Ne':'Neh',
  'Nm':'Num','Ob':'Obad','Pr':'Prov',
  'Ru':'Ruth','Zc':'Zech','Zp':'Zeph',
  '1Ch':'1Chr','2Ch':'2Chr','1K':'1Kgs','2K':'2Kgs',
  '1S':'1Sam','2S':'2Sam','Am':'Amos','SS':'Song',
  'Philem':'Phm','Pm':'Phm','Phlm':'Phm','Duet':'Deut',
  // English variant spellings, including the reading titles ("Matt.", "Psa.")
  'Matt':'Mt','Mar':'Mk','Luk':'Lk','Joh':'Jn',
  'Psa':'Ps','1Cor':'1Co','2Cor':'2Co','1Pet':'1Pe','2Pet':'2Pe',
};

const ABBR_PAT = '(?:\\d\\s*[A-Za-z]+\\.?|[\\u4e00-\\u9fff]{1,3}|[A-Za-z0-9]+(?:\\.[A-Za-z0-9]+)*\\.?)';
const VERSE_RE        = new RegExp(`^(${ABBR_PAT})\\s+(\\d+)\\s*:(\\d+)\\s*(.*)`);
const VERSE_RE_SINGLE = new RegExp(`^(${ABBR_PAT})\\s+(\\d+)\\s+(.*)`);
const NO_ABBR_RE      = /^(\d+)\s*:\s*(\d+)\s+(.*)/;

// Only these have no chapter number in their references. Requiring one keeps
// an ordinary poetic line such as "In 3 days…" from being read as a verse.
const SINGLE_CHAPTER = new Set(['Obad', 'Phm', '2Jn', '3Jn', 'Jude']);

export function normalizeBookAbbr(raw) {
  const stripped = raw.replace(/\./g, '').replace(/\s+/g, '');
  const abbr = ABBR_TO_BOOK[stripped] || stripped;
  return BOOK_NAMES[abbr] ? abbr : null;
}

// A chapter past the book's end is a typo in the source (e.g. "Pr. 145:1"),
// which the build skipped too, so there is no chapter row to match it to.
function validChapter(abbr, chapter) {
  return chapter >= 1 && chapter <= (BIBLE_VERSE_COUNTS[abbr]?.length || 0);
}

// The first book named in a reading title, "Isa. 46:1-49:26" → "Isa". Used for
// rows whose opening lines carry no book of their own.
export function bookFromTitle(title) {
  const m = (title || '').replace(/^(New|Old) Testament\s*-\s*/i, '').match(/^(.+?)\s+\d+\s*:/);
  return m ? normalizeBookAbbr(m[1].trim()) : null;
}

// Parses every line of one portion's text. Returns one entry per line:
// { abbr, chapter, verse, prefix, body } for a verse line, null for anything
// else (Psalm headings, the second line of a poetic verse, blank lines).
// `prefix` is the reference as written, for display beside the marked text.
// `body` is the verse text after it — the same text bible_chapters stores for
// the verse, which is what highlights and notes are anchored to.
export function parseReadingLines(text, title) {
  let lastBook = bookFromTitle(title);
  return (text || '').split('\n').map(raw => {
    const line = raw.trim();
    let abbr = null, chapter, verse, body;
    let m = line.match(VERSE_RE);
    if (m) {
      abbr = normalizeBookAbbr(m[1]);
      chapter = parseInt(m[2], 10); verse = parseInt(m[3], 10); body = m[4];
    } else if ((m = line.match(VERSE_RE_SINGLE))) {
      abbr = normalizeBookAbbr(m[1]);
      if (abbr && !SINGLE_CHAPTER.has(abbr)) abbr = null;
      chapter = 1; verse = parseInt(m[2], 10); body = m[3];
    } else if ((m = line.match(NO_ABBR_RE)) && lastBook) {
      // The build borrowed the book from the Chinese text; the line before
      // (or the title) names the same book and is simpler to reach from here.
      abbr = lastBook;
      chapter = parseInt(m[1], 10); verse = parseInt(m[2], 10); body = m[3];
    }
    if (!abbr || !validChapter(abbr, chapter)) return null;
    lastBook = abbr;
    return { abbr, chapter, verse, prefix: line.slice(0, line.length - body.length).trim(), body };
  });
}
